import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCampaigns,
  useDeleteCampaign,
  useDuplicateCampaign,
  useActivateCampaign,
  usePauseCampaign,
  useResumeCampaign,
  useCancelCampaign,
} from '../hooks/useCampaigns';
import { CampaignCard } from '../components/CampaignCard';
import { CampaignListItem } from '../components/CampaignListItem';
import { FiltersButton, Button, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, FilterBottomSheet, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { Icon } from '@/shared/icons';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { useSocket } from '@/contexts/SocketContext';
import {
  SOCKET_EVENTS,
  CampaignStatus,
  AudienceType,
  SchedulingType,
  AUDIENCE_TYPE_LABELS,
  SCHEDULING_TYPE_LABELS,
} from '@troia-v3/types';
import type { CampaignQuery, CampaignResponse } from '@troia-v3/types';

type SortKey = 'name' | 'status' | 'sent' | 'rate' | 'date';

const STATUS_LABELS: Record<CampaignStatus, string> = {
  [CampaignStatus.DRAFT]: 'Rascunho',
  [CampaignStatus.SCHEDULED]: 'Agendada',
  [CampaignStatus.IN_PROGRESS]: 'Em progresso',
  [CampaignStatus.PAUSED]: 'Pausada',
  [CampaignStatus.COMPLETED]: 'Concluída',
  [CampaignStatus.FAILED]: 'Falhou',
  [CampaignStatus.CANCELLED]: 'Cancelada',
};

const STATUS_ORDER: Record<string, number> = {
  in_progress: 0, scheduled: 1, paused: 2, draft: 3, completed: 4, failed: 5, cancelled: 6,
};

const successRateOf = (c: CampaignResponse): number => {
  const sent = c.stats?.messagesSent ?? 0;
  return sent > 0 ? ((c.stats?.messagesDelivered ?? 0) / sent) * 100 : -1;
};
const dateOf = (c: CampaignResponse): number =>
  new Date(c.scheduledFor ?? c.stats?.startedAt ?? c.createdAt ?? 0).getTime();

/**
 * Campaigns Page — listagem com toolbar 3 zonas, filterbar de chips, bulk bar,
 * views grid/table (mobile = cards) seguindo o padrão-lista do Design System.
 */
export const CampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  const confirm = useConfirm();

  // "+" central do bottom-nav mobile → nova campanha (mockup `.m-fab`).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); navigate('/prospecting/campaigns/new'); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, [navigate]);

  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState<CampaignQuery>({ page: 1, limit: 12 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'date', dir: 'desc' });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data, isLoading, error } = useCampaigns(query);
  const deleteCampaign = useDeleteCampaign();
  const duplicateCampaign = useDuplicateCampaign();
  const activateCampaign = useActivateCampaign();
  const pauseCampaign = usePauseCampaign();
  const resumeCampaign = useResumeCampaign();
  const cancelCampaign = useCancelCampaign();

  // Resolve channelId → nome amigável (rota /channels/dropdown, sem permissão de módulo).
  const { data: channelsData } = useChannelsDropdown();
  const channelMap = useMemo(
    () => new Map((channelsData ?? []).map((c) => [c.id, c.name])),
    [channelsData],
  );

  const items = useMemo(() => data?.items ?? [], [data]);

  // Sort client-side na página carregada (backend não expõe sort de campanhas).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'status') cmp = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
      else if (sort.key === 'sent') cmp = (a.stats?.messagesSent ?? 0) - (b.stats?.messagesSent ?? 0);
      else if (sort.key === 'rate') cmp = successRateOf(a) - successRateOf(b);
      else cmp = dateOf(a) - dateOf(b);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  // ── Socket: atualizações de status/progresso em tempo real ──
  const handleCampaignUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  }, [queryClient]);

  useEffect(() => {
    if (!socket) return;
    socket.on(SOCKET_EVENTS.CAMPAIGN_PROGRESS, handleCampaignUpdate);
    socket.on(SOCKET_EVENTS.CAMPAIGN_COMPLETED, handleCampaignUpdate);
    return () => {
      socket.off(SOCKET_EVENTS.CAMPAIGN_PROGRESS, handleCampaignUpdate);
      socket.off(SOCKET_EVENTS.CAMPAIGN_COMPLETED, handleCampaignUpdate);
    };
  }, [socket, handleCampaignUpdate]);

  // ── Seleção ──
  const selectedCount = selectedIds.size;
  const allOnPageSelected = items.length > 0 && items.every((c) => selectedIds.has(c.id));
  const someOnPageSelected = items.some((c) => selectedIds.has(c.id));

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((c) => next.delete(c.id));
      else items.forEach((c) => next.add(c.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  // ── Busca / filtros ──
  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const setStatusFilter = (status?: CampaignStatus) => {
    setQuery((prev) => ({ ...prev, status, page: 1 }));
    setFiltersOpen(false);
  };
  const setAudienceFilter = (audienceType?: AudienceType) => {
    setQuery((prev) => ({ ...prev, audienceType, page: 1 }));
    setFiltersOpen(false);
  };
  const setSchedulingFilter = (schedulingType?: SchedulingType) => {
    setQuery((prev) => ({ ...prev, schedulingType, page: 1 }));
    setFiltersOpen(false);
  };
  const clearAllFilters = () => {
    setQuery((prev) => ({ ...prev, status: undefined, audienceType: undefined, schedulingType: undefined, page: 1 }));
    setFiltersOpen(false);
  };

  const statusLabel = query.status && !Array.isArray(query.status) ? STATUS_LABELS[query.status] : null;
  const audienceLabel = query.audienceType ? AUDIENCE_TYPE_LABELS[query.audienceType] : null;
  const schedulingLabel = query.schedulingType ? SCHEDULING_TYPE_LABELS[query.schedulingType] : null;
  const activeFilterCount = [statusLabel, audienceLabel, schedulingLabel].filter(Boolean).length;

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  // ── Ações de ciclo de vida (confirmação preservada) ──
  const handleView = (id: string) => navigate(`/prospecting/campaigns/${id}`);
  const handleEdit = (id: string) => navigate(`/prospecting/campaigns/${id}/edit`);

  const handleDelete = async (id: string) => {
    if (await confirm({ message: 'Tem certeza que deseja excluir esta campanha?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' })) {
      await deleteCampaign.mutateAsync(id);
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };
  const handleDuplicate = async (id: string) => {
    const copy = await duplicateCampaign.mutateAsync(id);
    navigate(`/prospecting/campaigns/${copy.id}/edit`);
  };
  const handleActivate = async (id: string) => {
    if (await confirm({ message: 'Tem certeza que deseja ativar esta campanha?', variant: 'default', confirmButtonText: 'Ativar' })) await activateCampaign.mutateAsync(id);
  };
  const handlePause = async (id: string) => {
    if (await confirm({ message: 'Tem certeza que deseja pausar esta campanha?', variant: 'default', confirmButtonText: 'Pausar' })) await pauseCampaign.mutateAsync(id);
  };
  const handleResume = async (id: string) => {
    if (await confirm({ message: 'Tem certeza que deseja retomar esta campanha?', variant: 'default', confirmButtonText: 'Retomar' })) await resumeCampaign.mutateAsync(id);
  };
  const handleCancel = async (id: string) => {
    if (await confirm({ message: 'Tem certeza que deseja cancelar esta campanha? Esta ação não pode ser desfeita.', variant: 'default', confirmButtonText: 'Cancelar campanha' })) await cancelCampaign.mutateAsync(id);
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selectedCount} campanha(s) selecionada(s)?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    Array.from(selectedIds).forEach((id) => deleteCampaign.mutate(id));
    clearSelection();
  };

  const renderCheck = (on: boolean, mixed = false, onClick?: () => void, label?: string) => (
    <button
      type="button"
      onClick={onClick}
      className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors flex-shrink-0"
      style={{
        backgroundColor: on || mixed ? 'var(--theme-primary)' : 'transparent',
        borderColor: on || mixed ? 'var(--theme-primary)' : 'var(--theme-input-border)',
        color: '#ffffff',
      }}
      aria-label={label}
      data-testid="campaigns-select-all"
    >
      {on && <Icon name="check" className="w-3 h-3" />}
      {!on && mixed && <span className="w-2 h-0.5 rounded-full bg-white" />}
    </button>
  );

  const SortHead: React.FC<{ label: string; k: SortKey; className?: string }> = ({ label, k, className }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-bold whitespace-nowrap cursor-pointer select-none ${className ?? ''}`}
      style={{ color: 'var(--theme-muted-text)' }}
      onClick={() => handleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === k && <Icon name={sort.dir === 'asc' ? 'chevron-up' : 'chevron-down'} className="w-3 h-3" />}
      </span>
    </th>
  );

  const filterSection = (
    title: string,
    options: Array<{ value: string | undefined; label: string }>,
    current: string | undefined,
    onPick: (v: never) => void,
  ) => (
    <>
      <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>{title}</p>
      {options.map((opt) => {
        const active = current === opt.value;
        return (
          <button
            key={opt.label}
            onClick={() => onPick(opt.value as never)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <span className="truncate">{opt.label}</span>
            {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      {filterSection(
        'Status',
        [{ value: undefined, label: 'Todos' }, ...Object.values(CampaignStatus).map((s) => ({ value: s, label: STATUS_LABELS[s] }))],
        query.status && !Array.isArray(query.status) ? query.status : undefined,
        (v) => setStatusFilter(v),
      )}
      {filterSection(
        'Audiência',
        [{ value: undefined, label: 'Todas' }, ...Object.values(AudienceType).map((a) => ({ value: a, label: AUDIENCE_TYPE_LABELS[a] }))],
        query.audienceType,
        (v) => setAudienceFilter(v),
      )}
      {filterSection(
        'Agendamento',
        [{ value: undefined, label: 'Todos' }, ...Object.values(SchedulingType).map((s) => ({ value: s, label: SCHEDULING_TYPE_LABELS[s] }))],
        query.schedulingType,
        (v) => setSchedulingFilter(v),
      )}
    </>
  );

  const cardHandlers = {
    onView: handleView,
    onEdit: handleEdit,
    onDuplicate: handleDuplicate,
    onDelete: handleDelete,
    onActivate: handleActivate,
    onPause: handlePause,
    onResume: handleResume,
    onCancel: handleCancel,
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={
          <>
            {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium truncate hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }}>Prospecção</span>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Campanhas</span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
          </>
        }
        center={
          /* Centro: busca (sempre centralizada — F17-22) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar campanha…"
            data-testid="campaigns-search"
          />
        }
        right={
          <>
            {/* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTA */}
          {/* Filtros */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="campaigns-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="campaigns-filters-popover"
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="campaigns-view"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Nova campanha (único primário — F01-03: Button shared 38px, colapso @container) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => navigate('/prospecting/campaigns/new')}
            data-testid="new-campaign"
          >
            Nova campanha
          </Button>
          </>
        }
        mobile={
          /* `.m-sub` do mockup: busca sunken + Filtros (abre bottom-sheet) */
          <>
            <div
              className="flex items-center gap-2 h-[38px] px-3 rounded-[10px] flex-1 min-w-0"
              style={{ backgroundColor: 'var(--theme-surface-sunken, var(--theme-page-background))', border: '1px solid var(--theme-input-border)' }}
            >
              <Icon name="search" className="w-4 h-4 flex-none" style={{ color: 'var(--theme-muted-text)' }} />
              <input
                value={searchTerm}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar campanha…"
                className="flex-1 min-w-0 bg-transparent outline-none text-[13px]"
                style={{ color: 'var(--theme-input-text)' }}
                data-testid="campaigns-search-mobile"
              />
            </div>
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="campaigns-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {audienceLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Audiência: {audienceLabel}
              <button onClick={() => setAudienceFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de audiência">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {schedulingLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Agendamento: {schedulingLabel}
              <button onClick={() => setSchedulingFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de agendamento">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="campaigns-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionada(s)</span>
          <span className="flex-1" />
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="campaigns-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="campaigns-bulk-clear">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar campanhas. Tente novamente.</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando campanhas..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhuma campanha encontrada"
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Crie sua primeira campanha para começar a engajar seus clientes'}
              actionLabel="Nova campanha"
              onAction={() => navigate('/prospecting/campaigns/new')}
            />
          )}

          {/* Grid (Cards) — F34: cards sem checkbox, seleção só na view de lista */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedItems.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  channelName={channelMap.get(campaign.channelId)}
                  {...cardHandlers}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    channelName={channelMap.get(campaign.channelId)}
                    {...cardHandlers}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <th className="pl-4 pr-1 py-3 w-11">
                        {renderCheck(allOnPageSelected, someOnPageSelected && !allOnPageSelected, toggleSelectAllOnPage, 'Selecionar todos')}
                      </th>
                      <SortHead label="Campanha" k="name" />
                      <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Canal</th>
                      <SortHead label="Status" k="status" />
                      <SortHead label="Enviados" k="sent" />
                      <SortHead label="Taxa de Sucesso" k="rate" />
                      <SortHead label="Data" k="date" />
                      <th className="px-4 py-3 w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((campaign) => (
                      <CampaignListItem
                        key={campaign.id}
                        campaign={campaign}
                        channelName={channelMap.get(campaign.channelId)}
                        selected={selectedIds.has(campaign.id)}
                        onToggleSelect={toggleSelect}
                        {...cardHandlers}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação F48: totalItems/pageSize/itemLabel — "Mostrando X–Y de N campanhas" */}
          {!isLoading && !error && data?.page != null && data?.totalPages != null && (data.total ?? 0) > 0 && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
              totalItems={data.total}
              pageSize={query.limit ?? 12}
              itemLabel="campanhas"
            />
          )}
        </div>
      </div>

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="campaigns-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>
    </div>
  );
};
